import React, { useEffect, useState } from 'react'

import { uniqueId } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { getHeaderProps } from './config'
import { DataItemEditingDrawer } from '../DataItemDrawer'
import { HeaderActions } from './subComponents/Header.Actions'
import { HeaderImage } from './subComponents/Header.Image'
import { HeaderTags } from './subComponents/Header.Tags'
import { HeaderText } from './subComponents/Header.Text'
import { Card } from '@/Atomic/Atoms/Card'
import { MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import { getFilterDescription } from '@/Atomic/Organisms/FilterBuilder/utils/filterToReadable'
import { LogPhoneCallButton } from '@/Components/Activity/LogPhoneCall'
import { ConfirmationModal } from '@/Components/shared/modals/ConfirmationModal'
import { DataClassName } from '@/Data/const'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import { useModalHandler } from '@/hooks/useModalHandler'
import {
  getAutomationsProfileIdByEmail,
  navigateToAutomationsProfile,
} from '@/utils/automationsUtils'
import { useDataItemDelete } from '@/utils/CRUD/hooks/useDataItemDelete'
import { useDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getPisaSalesDeepLink } from '@/utils/getPisaSalesDeepLink'
import { getPlanningBoardLink } from '@/utils/getPlanningBoardLink'
import { getImageSrcFromDataItem } from '@/utils/image'
import { getDeleteModalSubtitle } from '@/Widgets/DXPDetails/utils/deleteModalSubtitle'
import { getDeleteModalTitle } from '@/Widgets/DXPDetails/utils/deleteModalTitle'

import './DataItemHeader.scss'
interface DataItemHeaderProps {
  dataItem: DataItem
}

export const DataItemHeader: React.FC<DataItemHeaderProps> = connect(
  ({ dataItem }) => {
    const { t, i18n } = useTranslation()
    const [automationsId, setAutomationsId] = useState<string>()

    useEffect(() => {
      const isPerson = dataItem.dataClassName() === DataClassName.PERSON
      const email = dataItem.get('email') as string | undefined
      if (isPerson && email) {
        getAutomationsProfileIdByEmail(email).then((profileId) => {
          setAutomationsId(profileId)
        })
      }
    }, [dataItem])

    // Edit
    const { isDrawerOpen, closeDrawer, openDrawer } = useDrawer()

    // Delete — the detail page is the one surface that must navigate away, since the
    // deleted record *is* the page (DXPDetailsWidgetComponent bails once it has no id).
    const { deleteDataItem } = useDataItemDelete(dataItem, {
      navigateToOverview: true,
    })
    const { isOpen, closeModal, openModal } = useModalHandler()

    // Data Display
    const dataClassName = dataItem.dataClassName()!

    const isList = dataClassName === DataClassName.LIST
    const listEntityName =
      (dataItem.get('entityType') as string | undefined)?.toLowerCase() ??
      'person'
    const { attributes: baseSchemaAttributes, relations: schemaRelations } =
      useEntitySchema(isList ? listEntityName : 'person')

    // MenuItems
    let pisaSalesMenuItem: MenuItemProps[] = []
    let planningBoardMenuItem: MenuItemProps[] = []
    let automationsMenuItem: MenuItemProps[] = []
    let generateDescriptionMenuItem: MenuItemProps[] = []

    const avatar = getImageSrcFromDataItem(dataItem)
    const title = dataItem.get('displayTitle') as string
    const { subtitle, link, tags, linkDataItem, icon } =
      getHeaderProps(dataItem)
    const deleteModalTitle = getDeleteModalTitle(dataClassName)
    const deleteModalSubtitle = getDeleteModalSubtitle(dataClassName)
    const supportsPhoneCall =
      dataClassName === DataClassName.PERSON ||
      dataClassName === DataClassName.ACTIVITY

    const deleteItem: MenuItemProps[] = [
      {
        icon: 'bin',
        label: t('common.delete'),
        type: 'danger',
        onClick: openModal,
      },
    ]

    // Links
    const pisaSalesLink = getPisaSalesDeepLink(dataItem)

    if (pisaSalesLink) {
      pisaSalesMenuItem = [
        {
          icon: 'link-external',
          label: t('common.openIn', { application: 'Classic CRM' }),
          type: 'default',
          onClick: () => window.open(pisaSalesLink, '_blank'),
        },
      ]
    }

    const planningBoardLink = getPlanningBoardLink(dataItem)
    if (planningBoardLink) {
      planningBoardMenuItem = [
        {
          icon: 'link-external',
          label: t('common.openIn', { application: 'Planning Board' }),
          type: 'default',
          onClick: () => window.open(planningBoardLink, '_blank'),
        },
      ]
    }

    if (automationsId) {
      automationsMenuItem = [
        {
          icon: 'email-opened',
          label: t('common.seeProfilePage'),
          type: 'default',
          onClick: () => navigateToAutomationsProfile(automationsId),
        },
      ]
    }

    if (isList) {
      generateDescriptionMenuItem = [
        {
          icon: 'sparkle',
          label: t('list.generateDescription'),
          type: 'default',
          onClick: async () => {
            const listQuery = dataItem.get('query') as
              | Record<string, unknown>
              | undefined
            const apiFilter = listQuery?.filters as
              | SavedFilterStructure
              | undefined
            if (!apiFilter?.or?.length) return

            const filterStructure = {
              groups: apiFilter.or.map((group) => ({
                id: uniqueId('group_'),
                conditions: group.and.map((cond) => ({
                  id: uniqueId('cond_'),
                  field: cond.field,
                  entity: listEntityName,
                  operator: cond.op,
                  value: cond.value,
                })),
              })),
            }

            const raw = await getFilterDescription(
              filterStructure,
              listEntityName,
              baseSchemaAttributes,
              schemaRelations,
            )
            const json = raw
              .replace(/^```(?:json)?\s*/i, '')
              .replace(/\s*```$/, '')
              .trim()
            const parsed = JSON.parse(json) as Record<string, string>
            const lang = i18n.language || 'en'
            const description =
              parsed[lang] ?? parsed[Object.keys(parsed)[0]!] ?? raw

            const existing = dataItem.get('description') as string | undefined
            const aiLabel = `✨ ${t('list.aiGenerated')}`
            const newDescription = existing
              ? `${existing}\n${description}\n${aiLabel}`
              : `${description}\n${aiLabel}`

            await dataItem.update({ description: newDescription })
          },
        },
      ]
    }

    const menuItems = Array.of(
      ...pisaSalesMenuItem,
      ...planningBoardMenuItem,
      ...automationsMenuItem,
      ...generateDescriptionMenuItem,
      ...deleteItem,
    )

    return (
      <Card className="data item header">
        <HeaderImage avatar={avatar} icon={icon} />
        <HeaderText
          title={title}
          subtitle={subtitle}
          link={link}
          linkDataItem={linkDataItem}
        />
        <HeaderTags tags={tags} />
        <HeaderActions
          menuItems={menuItems}
          onClick={openDrawer}
          extraAction={
            supportsPhoneCall ? (
              <LogPhoneCallButton
                dataItem={dataItem}
                buttonClassName="phone-call-header-button"
              />
            ) : undefined
          }
        />
        <ConfirmationModal
          variant="danger"
          isOpen={isOpen}
          close={closeModal}
          confirm={() => deleteDataItem()}
          title={t(deleteModalTitle)}
          subtitle={t(deleteModalSubtitle)}
        />
        <DataItemEditingDrawer
          dataItem={dataItem}
          isOpen={isDrawerOpen}
          close={closeDrawer}
        />
      </Card>
    )
  },
)
